// src/features/system/orchestrator/api/scopeOutcomesApi.ts
//
// Scope-based entity API layer (v2).
// Instead of dedicated /users, /rbac endpoints, we query the orchestrator's
// generic scopes + outcome-events endpoints filtered by namespace conventions.
//
// Namespace conventions:
//   rbac.user          → user scopes         (scope_key = canonical user identity)
//   rbac.role          → role scopes
//   rbac.permission    → permission/ability scopes
//   rbac.domain        → domain/tenant scopes
//   rbac.grant         → grant scopes (role→user, permission→role bindings)
//
// Each scope's "attributes" are outcome events written under that id_scope.
// Parent hierarchy is expressed via id_scope_father on the scope itself.

import { bff } from '@/lib/http/bff';

/* ─── Generic scope types ─────────────────────────────────── */

export interface ScopeEntity {
  id_scope: number;
  tenant_id: number;
  scope_namespace: string;
  scope_key: string;
  id_scope_father?: number | null;
  created_at: string;
  updated_at: string;
}

export interface ScopeOutcomeAttribute {
  outcome_id: number;
  id_scope: number;
  id_scope_father?: number | null;
  outcome_key: string;
  value: any;
  created_at: string;
  handler_key?: string;
  id_category: number;
  id_type: number;
}

export interface ScopeListResponse {
  items: ScopeEntity[];
  next_cursor?: number | null;
}

export interface OutcomeListResponse {
  items: ScopeOutcomeAttribute[];
  next_cursor?: { created_at: string; outcome_id: number } | null;
}

/* ─── Fetch scopes by namespace ───────────────────────────── */

export async function fetchScopesByNamespace(
  namespace: string,
  opts: {
    tenant_id?: number;
    id_scope_father?: number;
    limit?: number;
    cursor?: number;
  } = {},
): Promise<ScopeListResponse> {
  return bff.get<ScopeListResponse>('/orchestrator/scopes', {
    scope_namespace: namespace,
    tenant_id: opts.tenant_id,
    id_scope_father: opts.id_scope_father,
    limit: opts.limit ?? 200,
    cursor: opts.cursor,
  });
}

/* ─── Fetch outcome attributes for a scope ────────────────── */

export async function fetchScopeAttributes(
  idScope: number,
  opts: {
    tenant_id?: number;
    outcome_key?: string;
    handler_namespace?: string;
    limit?: number;
  } = {},
): Promise<OutcomeListResponse> {
  return bff.get<OutcomeListResponse>('/orchestrator/outcomes/steps', {
    id_scope: idScope,
    include_history: false,
    tenant_id: opts.tenant_id,
    outcome_key: opts.outcome_key,
    handler_namespace: opts.handler_namespace,
    limit: opts.limit ?? 200,
  });
}

/* ─── Convenience: fetch scopes + latest attributes in one shot ─── */

export interface ScopeEntityWithAttributes extends ScopeEntity {
  attributes: Record<string, any>;
}

export async function fetchScopesWithAttributes(
  namespace: string,
  opts: {
    tenant_id?: number;
    id_scope_father?: number;
    attributeKeys?: string[];
    limit?: number;
  } = {},
): Promise<ScopeEntityWithAttributes[]> {
  const { items: scopes } = await fetchScopesByNamespace(namespace, opts);

  // For each scope, fetch its latest attributes
  const enriched = await Promise.all(
    scopes.map(async (scope) => {
      const { items: outcomes } = await fetchScopeAttributes(scope.id_scope, {
        tenant_id: opts.tenant_id,
      });

      // Build attribute map: outcome_key → latest value
      const attributes: Record<string, any> = {};
      for (const o of outcomes) {
        // If we have specific keys filter, skip others
        if (opts.attributeKeys && !opts.attributeKeys.includes(o.outcome_key)) {
          continue;
        }
        // Latest wins (items come sorted by created_at DESC)
        if (!(o.outcome_key in attributes)) {
          attributes[o.outcome_key] = o.value;
        }
      }

      return { ...scope, attributes };
    }),
  );

  return enriched;
}

/* ─── Namespace constants ─────────────────────────────────── */

export const RBAC_NAMESPACES = {
  USER: 'rbac.user',
  ROLE: 'rbac.role',
  PERMISSION: 'rbac.permission',
  DOMAIN: 'rbac.domain',
  GRANT: 'rbac.grant',
} as const;
